import { fetcher, fetcherDelete, fetcherPost, fetcherWithMethod } from 'utils/axios';
import { createGenericCRUD } from './commonCrud';
import useSWR from 'swr';
import { handleApiError, showSuccess } from 'utils/errorHandler';
import { CrudEndpoints } from 'types/common';
import { AxiosRequestConfig } from 'axios';
import {
    DeleteTaskTemplateFilePayload,
    GenerateBlobFileIDPayload,
    GenerateUploadFileUrlsPayload,
    GetFileUrlParams,
    InsertTaskRequest,
    TaskTemplate,
    TaskTemplateLookup
} from 'types/PPFUser/taskTemplates';
import { useMemo } from 'react';

// Endpoints configuration
const endpoints: CrudEndpoints = {
    key: 'TaskTemplate',
    getList: '/GetList',
    getListById: '/GetList/{ID}',
    getModal: '',
    getLookupList: '/GetLookupList',
    insert: '',
    update: '',
    delete: '',
    getlookupPost: true
};

// Create CRUD instance
const taskTemplatetCRUD = createGenericCRUD<TaskTemplate>(endpoints);

export const useSaveAsTaskTemplate = async (projectID: number, data: { taskModule: number; taskTemplateName: string }) => {
    try {
        const response = await fetcherWithMethod('POST', [`${endpoints.key}/SaveAsTemplate/${projectID}`, { data, timeout: 0 }]);

        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const useSaveAsTaskTemplateGeneral = async (data: { taskIDs: number[]; taskTemplateName: string }) => {
    try {
        const response = await fetcherWithMethod('POST', [`${endpoints.key}/SaveAsTemplate`, { data, timeout: 0 }]);

        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const useApplyTaskTemplate = async (data: { projectID: number | null; taskTemplateID: number }) => {
    try {
        const response = await fetcherWithMethod('POST', [`${endpoints.key}/Apply`, { data }]);
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const useUpdateActiveStatus = async (data: { updatedOn: string | null; taskTemplateID: number; active: boolean }) => {
    try {
        const response = await fetcherWithMethod('POST', [`${endpoints.key}/UpdateActiveStatus`, { data }]);
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const useRestoreTaskTemplate = async (taskTemplateID: number) => {
    try {
        const response = await fetcherWithMethod('PUT', [`${endpoints.key}/RevertToDefault/${taskTemplateID}`, {}]);

        showSuccess('Template revert to default successfully');
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const useCheckDuplicateTaskTemplateName = async (taskTemplateName: string | null, excludeID?: number): Promise<boolean> => {
    if (!taskTemplateName) return false;

    const endpoint = `${endpoints.key}/CheckDuplicateTemplateName?TemplateName=${encodeURIComponent(taskTemplateName)}${excludeID ? `&ExcludeID=${excludeID}` : ''}`;

    try {
        const res = await fetcherWithMethod('GET', [endpoint, { method: 'GET' }]);
        return res as boolean;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const useCopyTaskTemplate = () => {
    const { mutate } = useSWR(`${endpoints.key}/GetList`);

    const copyTaskTemplate = async (data: { sourceTaskTemplateID: number; newTemplateName: string }) => {
        try {
            const response = await fetcherWithMethod('POST', [`${endpoints.key}/Copy`, { data } as AxiosRequestConfig]);
            mutate();

            return response;
        } catch (error) {
            handleApiError(error);
            throw error;
        }
    };

    return { copyTaskTemplate };
};

export function useGetTask(taskID?: number | null) {
    const fullEndpoint = taskID ? `${endpoints.key}/GetTask/${taskID}` : null;

    const { data, error, isLoading, mutate } = useSWR(fullEndpoint, (url: string) => fetcherWithMethod('GET', url), {
        revalidateIfStale: true,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        onError: (err) => handleApiError(err)
    });

    return {
        data: data ?? [],
        error,
        isLoading,
        mutate,
        hasError: !!error,
        retry: () => mutate()
    };
}

export const useInsertTask = async (data: InsertTaskRequest) => {
    try {
        const response = await fetcherWithMethod('POST', [`${endpoints.key}/InsertTask`, { data } as AxiosRequestConfig]);
        showSuccess('Task created successfully!');
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const useUpdateTask = async (data: InsertTaskRequest) => {
    try {
        const response = await fetcherWithMethod('PUT', [`${endpoints.key}/UpdateTask`, { data } as AxiosRequestConfig]);
        showSuccess('Task updated successfully!');
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const useDeleteTask = async (taskID: number) => {
    try {
        const response = await fetcherWithMethod('DELETE', [`${endpoints.key}/Deletetask/${taskID}`, {}]);
        showSuccess('Task deleted successfully!');

        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export function useGetTaskTemplateList(showAll: boolean = false) {
    const fullEndpoint = `${endpoints.key}${endpoints.getList}`;

    const { data, isLoading, error, isValidating, mutate } = useSWR(
        [fullEndpoint, { method: 'GET', params: { showAll } }],
        (args: [string, any]) => fetcherWithMethod('GET', args),
        {
            revalidateIfStale: true,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            onError: (err) => handleApiError(err)
        }
    );

    return useMemo(
        () => ({
            data: data?.records || data?.data || data || [],
            isLoading,
            error,
            mutate,
            isValidating,
            totalRecordCount: data?.totalCount ?? data?.totalRecordCount,
            isEmpty: !isLoading && !((data?.records || data?.data || data) && (Array.isArray(data?.data || data) ? (data?.data || data).length > 0 : true))
        }),
        [data, error, isLoading, isValidating, mutate]
    );
}

export function useGetTaskTemplateLookupList(taskModule: number) {
    const fullEndpoint = `${endpoints.key}${endpoints.getLookupList}/${taskModule}`;

    const { data, isLoading, error, isValidating, mutate } = useSWR<TaskTemplateLookup[]>(
        [fullEndpoint, { data: { includeIDs: [] } }],
        (args: [string, { data: { includeIDs: number[] } }]) => fetcherWithMethod('POST', args),
        {
            revalidateIfStale: true,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            onError: handleApiError
        }
    );

    return useMemo(
        () => ({
            data: data ?? [],
            isLoading,
            error,
            mutate,
            isValidating,
            isEmpty: !isLoading && Array.isArray(data) && data.length === 0
        }),
        [data, error, isLoading, isValidating, mutate]
    );
}

export function useGetTaskTemplateFiles(taskTemplateID: number, taskTemplateTaskID?: number | string | null) {
    const endpoint = taskTemplateTaskID ? `${endpoints.key}/GetFiles/${taskTemplateID}/${taskTemplateTaskID}` : null;

    const { data, isLoading, isValidating, error, mutate } = useSWR(endpoint, (url) => fetcher([url, { timeout: 0 }]), {
        revalidateIfStale: true,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    return useMemo(
        () => ({
            data: data || [],
            isLoading,
            isValidating,
            error,
            mutate
        }),
        [data, error, isLoading, isValidating, mutate]
    );
}

export const useGetTaskTemplateFileUrl = async ({ taskTemplateID, taskTemplateTaskID, fileID }: GetFileUrlParams) => {
    try {
        const params: string[] = [];
        if (taskTemplateID !== undefined && taskTemplateID !== null) params.push(`TaskTemplateID=${encodeURIComponent(taskTemplateID)}`);
        if (taskTemplateTaskID !== undefined && taskTemplateTaskID !== null)
            params.push(`TaskTemplateTaskID=${encodeURIComponent(taskTemplateTaskID)}`);
        if (fileID && fileID !== 'undefined' && fileID !== 'null' && fileID !== '0') {
            params.push(`fileID=${encodeURIComponent(fileID)}`);
        }
        const queryString = params.length > 0 ? `?${params.join('&')}` : '';

        const response = await fetcher(`${endpoints.key}/GetFileUrl${queryString}`);
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const useGenerateUploadFileUrls = async (payload: GenerateUploadFileUrlsPayload) => {
    try {
        const response = await fetcherPost([`${endpoints.key}/GenerateUploadFileUrls`, { data: payload }]);
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const useAfterSuccessfulFileUpload = async (payload: GenerateBlobFileIDPayload) => {
    try {
        const response = await fetcherPost([`${endpoints.key}/GenerateBlobFileID`, { data: payload }]);
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

// 5. DeleteFile (DELETE)
export const useDeleteTaskFile = async (payload: DeleteTaskTemplateFilePayload) => {
    try {
        const response = await fetcherDelete([`${endpoints.key}/DeleteFile`, { data: payload }]);
        showSuccess('File deleted successfully');
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const {
    useGetListByID: useGetTaskTemplateByID,
    useUpdateRecord: useUpdateTaskTemplate,
    useInsertRecord: useInsertTaskTemplate,
    useGetModal: useGetTaskTemplateModal,
    useDeleteRecord: useDeleteTaskTemplate
} = taskTemplatetCRUD;
